import React, { useState } from 'react'
import { Spinner } from '@fluentui/react'
import {
  dollars,
  FundReleaseType,
  FundingSchedule,
  FUND_RELEASE_LOAN_HELP_LINK,
  getFundReleaseDetails,
} from '@oneethos/shared'

import { Section } from '../components'
import FundReleaseError from './fundReleaseError'

export interface FundReleasePageData {
  _id: string
  amount: number
  type: FundReleaseType
  companyName: string
  hasAgreed: boolean
  fundingSchedule: FundingSchedule
}

interface FundingReleasePageViewProps {
  pendingAgreement: FundReleasePageData
  onApprove: () => Promise<boolean>
}

export const FundingReleasePageView = ({
  pendingAgreement,
  onApprove,
}: FundingReleasePageViewProps): React.ReactElement => {
  const [agreed, setAgreed] = useState<boolean>(false)
  const [attested, setAttested] = useState<boolean>(false)
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)

  const details = getFundReleaseDetails(
    pendingAgreement.type,
    pendingAgreement.companyName,
    pendingAgreement.fundingSchedule,
  )

  const handleApprove = async (): Promise<void> => {
    setLoading(true)

    try {
      const wasApproved = await onApprove()
      setIsSubmitted(wasApproved)
    } finally {
      setLoading(false)
    }
  }

  if (!details) {
    return (
      <Section>
        <FundReleaseError error="generic" />
      </Section>
    )
  }

  if (isSubmitted) {
    return (
      <Section>
        <div className="text-center">
          <h3>Thank you!</h3>
          <p>
            Your approval has been documented. Once your installer has provided all
            necessary documentation for this milestone, we will release the approved
            funds to them.
          </p>
        </div>
      </Section>
    )
  }

  if (pendingAgreement.hasAgreed) {
    return (
      <Section>
        <div className="text-center">
          <h3>Fund Release Already Approved</h3>
          <p>
            You have already approved the fund release for this request.
            We'll notify you if any further action is required.
          </p>
        </div>
      </Section>
    )
  }

  return (
    <Section>
      <h3 className="text-center">Fund Release Request</h3>
      <div className="mb-4">
        <p><strong>Amount:</strong> {dollars(pendingAgreement.amount)}</p>
        <p>{details.description}</p>
      </div>

      <div className="mb-4">
        <h5>By approving this request, you certify the following:</h5>
        <ul className="mb-4">
          {details.requirements.map((requirement) => (
            <li key={requirement.title} className="mb-1">
              <strong>{requirement.title}</strong> {requirement.detail}
            </li>
          ))}
        </ul>
      </div>

      <div className="mb-4">
        <h5>Solar Project Funding and Tax Incentive Disclosure</h5>
        <p>
          If your intention is to proceed with your solar installation project in order to capture federal tax
          incentives that may soon expire, we recommend to not authorize the release of loan or funding proceeds
          until you have:
        </p>
        <ul>
          <li>
            <b>Consulted with your accountant or tax advisor </b>
            to confirm your eligibility for the intended tax incentives.
          </li>
          <li>
            <b>Confirmed directly with your solar installer </b>
            that your project will be completed within the required timeframe to qualify for those incentives.
          </li>
        </ul>
        <p>By approving the release of funds, you acknowledge and agree that: </p>
        <ul>
          <li><b>Climate First Bank has no control over your contract with the solar installer.</b></li>
          <li>
            <b>Climate First Bank does not provide tax advice </b>
            and makes no representations regarding your eligibility for any tax incentives, rebates, or credits.
          </li>
          <li>
            <b>You have independently consulted with your accountant/tax advisor and your solar installer </b>
            and confirmed that proceeding with the fund release is in your best interest.
          </li>
          <li>
            <b>You release and hold Climate First Bank harmless </b>
            from any liability, loss, or damages arising from (a) delays or non-completion of the project, (b)
            ineligibility for any tax incentive, or (c) any other matter related to your contractual arrangement
            with the installer or your personal tax situation.
          </li>
        </ul>
        <p>
          By proceeding with the authorization of funds release, you acknowledge your full understanding and acceptance
          of the terms stated above, and that you are not relying on any representation or warranty from
          Climate First Bank.
        </p>
      </div>
      <p>
        <strong>Help Article:</strong><br />
        <a
          href={FUND_RELEASE_LOAN_HELP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3"
        >
          Learn more about the fund release process and what it means for your project
        </a>
      </p>

      <div className="form-group mt-4">
        <label className="d-flex align-items-center gap-2">
          <input
            type="checkbox"
            checked={attested}
            onChange={() => setAttested(!attested)}
          />
          <b>
            I attest that the loan proceeds will not be used to satisfy any other financing arrangements,
            including leases, and that no other financial institution participated in financing this project.
          </b>
        </label>
        <label className="d-flex align-items-center gap-2 mb-2">
          <input
            type="checkbox"
            checked={agreed}
            onChange={() => setAgreed(!agreed)}
          />
          <b>I approve the release of funds to {pendingAgreement.companyName}.</b>
        </label>
      </div>

      <div className="text-center mt-3">
        <button
          className="btn btn-primary"
          disabled={!agreed || !attested || loading}
          onClick={handleApprove}
        >
          {loading ? <Spinner /> : 'Approve Request'}
        </button>
      </div>
    </Section>
  )
}

export default FundingReleasePageView
